import { useMemo, useState } from 'react'
import { Checkbox } from '@truepill/react-capsule'
import { useQuery } from '@truepill/tpos-react-router'
import SearchAutoComplete from 'components/SearchAutoComplete'
import { SearchResultLozenge, SearchResultLozengeList } from 'components/Tiles/Lozenge'
import { SEARCH_ORDER_TRIAGE_REASONS, SEARCH_COPAY_TRIAGE_REASONS } from 'gql'
import useFulfillmentQueue from 'hooks/useFulfillmentQueue'
import { usePlusClient } from 'providers/VisionRouter'
import { borderColor } from 'styles/styleVariables'
import { TriageReasonMap, CopayTriageReasonMap } from 'utils'

interface TriageReason {
  key: string
  value: string
}

interface TriageReasonSuggestion extends TriageReason {
  highlighted?: boolean
  selected?: boolean
  suggestionKey: string
}

interface Error {
  error: string
}

interface Loading {
  loading: boolean
}

type TriageReasonSuggestionItem = TriageReasonSuggestion | Error | Loading

interface TriageSearchProps {
  isVisible?: boolean
  hotKey?: string
  isModal?: boolean
  nameSpace?: string
  isCopay?: boolean
}

const TriageSearch = (props: TriageSearchProps): JSX.Element => {
  const { isVisible, hotKey = 'l', isModal, isCopay = false } = props
  const {
    currentLocation,
    routeToMergedQuery,
    QueryToolkit: { StringBoolean },
  } = usePlusClient()
  const [searchTerm, setSearchTerm] = useState('')
  const fulfillmentQueue = useFulfillmentQueue()

  const query = isCopay ? SEARCH_COPAY_TRIAGE_REASONS : SEARCH_ORDER_TRIAGE_REASONS

  const queryMap = currentLocation?.queryMap || {}

  const copayTriageReasonsVariables = {
    locationIds: queryMap?.locationIds,
    orderStages: fulfillmentQueue?.orderStages ?? [],
    fillStages: fulfillmentQueue?.fillStages ?? [],
  }

  const orderTriageReasonsVariables = {
    selectedLocationIds: queryMap?.locationIds,
    orderStages: fulfillmentQueue?.orderStages ?? [],
    ...(queryMap.medications && { medicationNames: queryMap.medications }),
    ...(queryMap.customerIds && { customerIds: queryMap.customerIds }),
    ...(queryMap.otcMedications && { otcMedicationNames: queryMap.otcMedications }),
    ...(queryMap.paymentType && { paymentType: queryMap.paymentType }),
    ...(queryMap.states?.length && { states: queryMap.states }),
    ...(queryMap.shelfLocations && { shelfLocations: queryMap.shelfLocations }),
    ...(queryMap.shippingMethods && { shippingMethods: queryMap.shippingMethods }),
    ...(queryMap.podNames && { selectedPodNames: queryMap.podNames }),
    ...(queryMap.batchOrders && { isBatch: StringBoolean(queryMap.batchOrders) }),
    ...(queryMap.reservedForInvestigation && {
      isReservedForInvestigation: StringBoolean(queryMap.reservedForInvestigation),
    }),
    ...{ isOtcOnly: StringBoolean(queryMap.otcOnly) || false },
    ...{ isRxOtcOnly: StringBoolean(queryMap.rxOtcOnly) || false },
    ...(queryMap.specialHandlingTags?.length && { specialHandlingTags: queryMap.specialHandlingTags }),
    ...(queryMap.multiPetOrders && { isMultiPet: StringBoolean(queryMap.multiPetOrders) }),
  }

  const { data, loading, error } = useQuery(query, {
    variables: isCopay ? copayTriageReasonsVariables : orderTriageReasonsVariables,
  })

  const triageMap = isCopay ? CopayTriageReasonMap : TriageReasonMap

  const selectedTriageOptions: string[] = useMemo(() => {
    return currentLocation?.queryMap?.triageReasons ? currentLocation.queryMap.triageReasons : []
  }, [currentLocation?.queryMap?.triageReasons])

  const searchTriageReasons = useMemo(() => {
    if (data?.searchOrderTriageReasons)
      return data.searchOrderTriageReasons.map((triage: { _id: string }) => triage._id)
    if (data?.searchCopayTriageReasons)
      return data?.searchCopayTriageReasons.map((triage: { _id: string }) => triage._id)
    return []
  }, [data?.searchOrderTriageReasons, data?.searchCopayTriageReasons])

  const selectAllText = selectedTriageOptions.length ? 'Deselect All' : 'Select All'
  const triageOptions = Object.keys(triageMap).map(key => ({ key, value: triageMap[key] }))

  const triageReasonOptions: TriageReason[] = useMemo(() => {
    const allSelectedOption = { key: 'All', value: selectAllText }
    return [
      allSelectedOption,
      ...triageOptions
        .sort((a: TriageReason, b: TriageReason) => {
          if (a.value < b.value) return -1
          if (a.value > b.value) return 1
          return 0
        })
        .filter((reason: TriageReason) => searchTriageReasons?.length && searchTriageReasons.includes(reason.key)),
    ]
  }, [selectAllText, triageOptions, searchTriageReasons])

  return (
    <>
      <SearchAutoComplete
        hotKey={isVisible ? hotKey : ''}
        isModal={isModal}
        preventScrolling={false}
        iconColor={borderColor}
        placeholder={'Search by reason....'}
        autoCompleteSuggestions={
          error
            ? [{ error: `Error searching Triage Reasons: ${JSON.stringify(error)}` }]
            : loading
            ? [{ loading }]
            : triageReasonOptions?.filter(c => c.value.toLowerCase().startsWith(searchTerm.toLowerCase()))
        }
        value={searchTerm}
        showOptionsOnFocus={true}
        onSelect={(triageReason: TriageReason) => {
          const reasonKey = triageReason.key
          routeToMergedQuery({ triageReasons: [...selectedTriageOptions, reasonKey] })
          setSearchTerm('')
        }}
        suggestionComponent={optionProps => (
          <AutoCompleteOption
            triageReasonSuggestionItem={optionProps as TriageReasonSuggestionItem}
            routeToMergedQuery={routeToMergedQuery}
            selectedReasons={selectedTriageOptions}
            triageReasonOptions={triageReasonOptions}
          />
        )}
        onChange={setSearchTerm}
        maximumHeight="16rem"
      />
      <SearchResultLozengeList>
        {selectedTriageOptions.map((reasonKey: any) => {
          return (
            <SearchResultLozenge
              key={reasonKey}
              closeCallback={() => {
                if (selectedTriageOptions.includes(reasonKey)) {
                  const newReasonKeys = [...selectedTriageOptions]
                  newReasonKeys.splice(selectedTriageOptions.indexOf(reasonKey), 1)
                  if (reasonKey === 'All') {
                    routeToMergedQuery({ triageReasons: [] })
                  } else routeToMergedQuery({ triageReasons: newReasonKeys })
                }
              }}
            >
              <p>{triageMap[reasonKey] || reasonKey}</p>
            </SearchResultLozenge>
          )
        })}
      </SearchResultLozengeList>
    </>
  )
}
type AutoCompleteOptionProps = {
  triageReasonSuggestionItem: TriageReasonSuggestionItem
  selectedReasons: string[]
  triageReasonOptions: TriageReason[]
  routeToMergedQuery: (toMerge: any) => void
}

const AutoCompleteOption = (props: AutoCompleteOptionProps) => {
  const { triageReasonSuggestionItem, selectedReasons, triageReasonOptions, routeToMergedQuery } = props
  if ((triageReasonSuggestionItem as Error).error) {
    return (
      <Checkbox {...props}>
        <p>{(triageReasonSuggestionItem as Error).error}</p>
      </Checkbox>
    )
  }

  if ((triageReasonSuggestionItem as Loading).loading) {
    return (
      <Checkbox {...triageReasonSuggestionItem}>
        <p>Searching...</p>
      </Checkbox>
    )
  }

  const triageReason = triageReasonSuggestionItem as TriageReasonSuggestion
  return (
    <Checkbox
      css={autoCompleteListItemCSS}
      label={triageReason.value}
      checked={selectedReasons.includes(triageReason.suggestionKey)}
      onCheckedChange={checked => {
        if (checked) {
          if (triageReason.suggestionKey === 'All') {
            routeToMergedQuery({ triageReasons: triageReasonOptions.map(reason => reason.key) })
          } else {
            if (selectedReasons.length + 2 === triageReasonOptions.length) {
              routeToMergedQuery({ triageReasons: triageReasonOptions.map(reason => reason.key) })
            } else {
              routeToMergedQuery({ triageReasons: [...selectedReasons, triageReason.suggestionKey] })
            }
          }
        } else {
          if (triageReason.suggestionKey === 'All') {
            routeToMergedQuery({ triageReasons: [] })
          } else {
            if (selectedReasons.includes(triageReason.suggestionKey)) {
              const remainingKeys = selectedReasons.filter(
                reason => reason !== triageReason.suggestionKey && reason !== 'All',
              )
              routeToMergedQuery({ triageReasons: remainingKeys })
            }
          }
        }
      }}
    >
      <p>{triageReason.value}</p>
    </Checkbox>
  )
}

const autoCompleteListItemCSS = {
  margin: '0.15rem',
  border: '1rem',
}

export default TriageSearch
